import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, Globe } from 'lucide-react';
interface SlideProps {
  index: number;
  total: number;
}
const FACES = {
  global: 'https://images.unsplash.com/photo-1612531386530-97286d97c2d2?w=1000&q=80',
  uk: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=600&q=80'
};
function GlobalWebinarViz() {
  return <div className="w-full h-full flex flex-col gap-2" data-id="element-95">
      <div className="relative w-full rounded-xl overflow-hidden bg-black flex-1 min-h-0" data-id="element-96">
        <img src={FACES.global} alt="Global Webinar" className="absolute inset-0 w-full h-full object-cover" data-id="element-97" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" data-id="element-98" />
        <span className="absolute top-2.5 left-2.5 bg-[var(--brand-green)] text-white text-[0.55rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded" data-id="element-99">
          Global Broadcast
        </span>
        <div className="absolute inset-x-0 bottom-0 p-3" data-id="element-100">
          <p className="text-white font-bold text-[clamp(0.9rem,1.25vw,1.25rem)] leading-tight" data-id="element-101">
            Atopic Dermatitis: The Series
          </p>
          <p className="text-white/60 text-[clamp(0.6rem,0.8vw,0.8rem)] mb-2" data-id="element-102">
            Full webinar · ~60 min
          </p>
          <span className="inline-flex items-center gap-1.5 bg-white text-black text-[clamp(0.6rem,0.8vw,0.8rem)] font-bold px-2.5 py-1 rounded" data-id="element-103">
            <Play className="w-3 h-3 fill-black" data-id="element-104" /> Play
          </span>
        </div>
      </div>
    </div>;
}
function UKEpisodesViz() {
  const eps = [{
    n: 1,
    t: 'Where AD Hurts Most'
  }, {
    n: 2,
    t: 'Beyond The Skin'
  }, {
    n: 3,
    t: 'Barriers To Care'
  }];
  return <div className="w-full h-full flex flex-col" data-id="element-105">
      <div className="flex items-center gap-1.5 mb-2 flex-shrink-0" data-id="element-106">
        <Globe className="w-3.5 h-3.5 text-[var(--brand-green)]" data-id="element-107" />
        <p className="text-[var(--fg)] text-[clamp(0.7rem,0.9vw,0.9rem)] font-bold" data-id="element-108">
          Localized for the UK
        </p>
      </div>
      <div className="flex-1 grid grid-cols-3 gap-2 min-h-0" data-id="element-109">
        {eps.map((ep, i) => <motion.div key={ep.n} initial={{
        opacity: 0,
        x: 12
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.55 + i * 0.1,
        duration: 0.35
      }} className="relative rounded-lg overflow-hidden bg-black h-full" data-id="element-110">
            <img src={FACES.uk} alt={ep.t} className="absolute inset-0 w-full h-full object-cover" data-id="element-111" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" data-id="element-112" />
            <span className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded px-1.5 py-0.5" data-id="element-113">
              <span className="text-[0.7rem]" data-id="element-114">🇬🇧</span>
              <span className="text-[var(--brand-green)] text-[0.55rem] font-bold" data-id="element-115">
                UK
              </span>
            </span>
            <span className="absolute top-1.5 right-1.5 bg-black/70 text-white text-[0.55rem] font-bold px-1.5 py-0.5 rounded" data-id="element-116">
              5 min
            </span>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center" data-id="element-117">
              <Play className="w-4 h-4 fill-[var(--brand-green)] text-[var(--brand-green)] ml-0.5" data-id="element-118" />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-2" data-id="element-119">
              <p className="text-white/70 text-[0.55rem] font-semibold" data-id="element-120">
                EP {ep.n}
              </p>
              <p className="text-white text-[clamp(0.6rem,0.8vw,0.8rem)] font-bold leading-tight" data-id="element-121">
                {ep.t}
              </p>
            </div>
          </motion.div>)}
      </div>
      <p className="text-[var(--brand-green)] text-[clamp(0.65rem,0.85vw,0.85rem)] font-semibold mt-2 flex-shrink-0" data-id="element-122">
        Bite-sized, UK-relevant content
      </p>
    </div>;
}
export function Slide3UKConcept({
  index
}: SlideProps) {
  const steps = [{
    number: '1',
    tag: 'Source',
    title: 'Global Webinar',
    desc: 'The full 60-minute global broadcast provides the scientific foundation.',
    Viz: GlobalWebinarViz
  }, {
    number: '2',
    tag: 'Extraction',
    title: 'UK Localized Episodes',
    desc: 'Key chapters are extracted, localized, and adapted into 5-minute episodes for the UK market.',
    Viz: UKEpisodesViz
  }];
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)] p-[clamp(2rem,4vw,4rem)] flex flex-col" data-id="element-123">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(1rem,2vw,2rem)] flex-shrink-0" data-id="element-124">
        <p className="text-[var(--brand-green)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider mb-[clamp(0.4rem,0.8vw,0.8rem)]" data-id="element-125">
          The Concept
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(1.75rem,3.2vw,2.75rem)] leading-[1.1]" data-id="element-126">
          From Global Broadcast to{' '}
          <span className="text-[var(--brand-green-light)]" data-id="element-127">
            UK Episodic Content
          </span>
        </h2>
      </motion.div>

      <div className="flex-1 flex items-stretch gap-[clamp(0.75rem,1.5vw,1.5rem)] min-h-0" data-id="element-128">
        {steps.map((step, idx) => {
        const Viz = step.Viz;
        return <Fragment key={step.number} data-id="element-129">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3 + idx * 0.15,
            duration: 0.5
          }} className="flex-1 min-w-0 min-h-0" data-id="element-130">
                <div className="bg-[var(--surface)] rounded-2xl p-[clamp(0.875rem,1.4vw,1.4rem)] h-full flex flex-col border border-black/5 shadow-sm" data-id="element-131">
                  <div className="flex items-start gap-3 mb-[clamp(0.6rem,1vw,1rem)] flex-shrink-0" data-id="element-132">
                    <div className="w-9 h-9 rounded-full bg-[var(--brand-green)] flex items-center justify-center flex-shrink-0" data-id="element-133">
                      <span className="text-white font-bold text-base" data-id="element-134">
                        {step.number}
                      </span>
                    </div>
                    <div className="min-w-0" data-id="element-135">
                      <span className="text-[var(--brand-green-light)] font-bold text-[0.65rem] uppercase tracking-widest block" data-id="element-136">
                        Step {step.number} · {step.tag}
                      </span>
                      <h3 className="text-[var(--brand-green)] font-bold text-[clamp(1rem,1.45vw,1.4rem)] leading-tight" data-id="element-137">
                        {step.title}
                      </h3>
                    </div>
                  </div>
                  <div className="flex-1 min-h-0" data-id="element-138">
                    <Viz data-id="element-139" />
                  </div>
                  <p className="text-[var(--muted)] text-[clamp(0.7rem,0.9vw,0.9rem)] leading-snug mt-[clamp(0.6rem,1vw,1rem)] flex-shrink-0" data-id="element-140">
                    {step.desc}
                  </p>
                </div>
              </motion.div>

              {idx < steps.length - 1 && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.6 + idx * 0.15,
            duration: 0.4
          }} className="flex-shrink-0 flex items-center" data-id="element-141">
                  <div className="w-9 h-9 rounded-full bg-[var(--brand-green)] flex items-center justify-center shadow-sm" data-id="element-142">
                    <ArrowRight className="w-5 h-5 text-white" data-id="element-143" />
                  </div>
                </motion.div>}
            </Fragment>;
      })}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1,
      duration: 0.6
    }} className="flex justify-between items-end mt-[clamp(1rem,2vw,2rem)] flex-shrink-0" data-id="element-144">
        <p className="text-[var(--fg)] text-[clamp(1rem,1.25vw,1.25rem)] font-semibold" data-id="element-145">
          Creating highly relevant, easily consumable content for UK HCPs.
        </p>
      </motion.div>
    </div>;
}